import { isEmpty } from "lodash";
import {
  Maybe,
  Parameter,
  ResourceConfiguration,
} from "../../graphql/generated";

export class BPResourceConfiguration implements ResourceConfiguration {
  id?: Maybe<string> | undefined;
  name?: Maybe<string> | undefined;
  displayName: Maybe<string> | undefined;
  type?: Maybe<string> | undefined;
  parameters?: Maybe<Parameter[]> | undefined;
  processors?: Maybe<ResourceConfiguration[]> | undefined;
  disabled: boolean;
  constructor(rc?: ResourceConfiguration) {
    this.id = rc?.id;
    this.name = rc?.name;
    this.displayName = rc?.displayName;
    this.type = rc?.type;
    this.parameters = rc?.parameters;
    this.processors = rc?.processors;
    this.disabled = rc?.disabled ?? false;
  }

  isInline(): boolean {
    return isEmpty(this.name);
  }

  hasConfigurationParameters(): boolean {
    return this.parameters != null && this.parameters.length > 0;
  }

  // setParamsFromMap will set the parameters from Record<string, any>.
  // If the "name" key is specified it will set the name field of the ResourceConfiguration.
  // If the "processors" key is specified it will set the processors value.
  // It will not set undefined or null values to parameters.
  setParamsFromMap(map: Record<string, any>) {
    // Set name field if present
    if (map.name != null && map.name !== "") {
      this.name = map.name;
      delete map.name;
    }

    // Set displayName field if present
    if (map.displayName != null) {
      this.displayName = map.displayName;
      delete map.displayName;
    }

    // Set processors field if present
    if (map.processors != null) {
      this.processors = map.processors;
      delete map.processors;
    }

    // Set the parameters only if their values are not nullish.
    const parameters = Object.entries(map).reduce<Parameter[]>(
      (params, [name, value]) => {
        if (value != null) {
          params.push({ name, value });
        }
        return params;
      },
      [],
    );

    this.parameters = parameters;
  }
}

// getParameterValue will return the value of the parameter with the given name or a
// defaultValue if it doesn't exist.
export function getParameterValue<T>(
  rc: ResourceConfiguration,
  name: string,
  defaultValue?: T,
): T | undefined {
  const parameter = rc.parameters?.find((p) => p.name === name);
  return parameter?.value ?? defaultValue;
}

// equalsParameterValue will return true if the parameter with the given name has the
// given value. null and undefined are treated as equal.
export function equalsParameterValue<T>(
  rc: ResourceConfiguration,
  name: string,
  value: T | undefined | null,
): boolean {
  if (value == null) {
    // handle value == null or value == undefined
    return getParameterValue(rc, name) == null;
  }
  return getParameterValue(rc, name) === value;
}

// editParameterValue will update the value of the parameter with the given name, adding a
// new parameter if it does not exist.
export function editParameterValue<T>(
  rc: ResourceConfiguration,
  name: string,
  updater: (value?: T) => T,
): T {
  const parameter = rc.parameters?.find((p) => p.name === name);
  if (parameter != null) {
    parameter.value = updater(parameter.value);
    return parameter.value;
  } else {
    if (rc.parameters == null) {
      rc.parameters = [];
    }
    const value = updater(undefined);
    rc.parameters.push({ name, value });
    return value;
  }
}
